import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAfter } from "date-fns";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { AddHouseModal } from "@features/AddHouseModal/AddHouseModal";
import { PowerOfAttorneyDokobitConsentModal } from "@features/PowerOfAttorneyDokobitConsentModal/PowerOfAttorneyDokobitConsentModal";
import { Button } from "@components/Button";
import { ClassicLoader } from "@components/ClassicLoader/ClassicLoader";
import { PlusIcon } from "@components/icons";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { Link } from "@components/Link/Link";
import { Modal } from "@components/Modal/Modal";
import { Skeleton } from "@components/Skeleton/Skeleton";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useModalActions, type IUseModalProps } from "@store/modal/modal";
import { usePOAStore } from "@store/poaStore/poaStore";
import { useCustomerDetailsQuery } from "@services/customer/queries";
import { useOverviewQuery } from "@services/overview/queries";
import { useSubmitPOAServiceProviderRequestsMutation } from "@services/powersOfAttorney/mutations";
import {
  usePOASignedDocumentMetadataQuery,
  usePOASignedDocumentQuery,
} from "@services/powersOfAttorney/queries";
import { openPdfPreview } from "@utils/common";
import { formatDate } from "@utils/units";
import { PowerOfAttorneyHouseAccordionDataWrapper } from "./components/PowerOfAttorneyHouseAccordionDataWrapper";
import { POAServiceProviderSource } from "./types";
import {
  usePowerOfAttorneyBillFormSchema,
  type TPowerOfAttorneyBillFormSchema,
} from "./usePowerOfAttorneyBillFormSchema";
import { useReccomendationQuery } from "./useReccomendationQuery";

interface IPowerOfAttorneyBillFormModalProps extends IUseModalProps {
  showExistingConsent?: boolean;
  powerOfAttorneyLedgerConsentId?: string;
  shouldFetchAllRecommendations?: boolean;
}

export const PowerOfAttorneyBillFormModal: React.FC<
  IPowerOfAttorneyBillFormModalProps
> = (props) => {
  const {
    powerOfAttorneyLedgerConsentId,
    showExistingConsent,
    shouldFetchAllRecommendations,
    ...rest
  } = props;
  const modal = useModal(rest);
  const { create } = useModalActions();
  const { t } = useTranslation();
  const { providerList, reset } = usePOAStore();

  const { data: customerDetails } = useCustomerDetailsQuery();
  const { data: overviewData } = useOverviewQuery();
  const { data: documentMetadata } = usePOASignedDocumentMetadataQuery();
  const { data: signedDocument } = usePOASignedDocumentQuery();

  const {
    isReccomendationQueryLoading,
    useRecommendationQueryFn,
    reccomendationSource,
    useFallbackRecommendationQueryFn,
  } = useReccomendationQuery(
    powerOfAttorneyLedgerConsentId,
    shouldFetchAllRecommendations,
  );

  const { mutateAsync, isPending } =
    useSubmitPOAServiceProviderRequestsMutation();

  const { toastApiError } = useApiErrorUtils();

  const schema = usePowerOfAttorneyBillFormSchema();

  const { handleSubmit, control } = useForm<TPowerOfAttorneyBillFormSchema>({
    defaultValues: {
      authorization: showExistingConsent ? true : false,
      consent: showExistingConsent ? true : false,
      privacy: showExistingConsent ? true : false,
    },
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<TPowerOfAttorneyBillFormSchema> = async (
    _data,
  ) => {
    if (!overviewData) {
      return;
    }

    const flatProviderList = Object.entries(providerList).flatMap(
      ([jointBillServiceId, providers]) =>
        providers
          .filter((provider) => provider.selected)
          .map((provider) => ({
            jointBillServiceId: Number(jointBillServiceId),
            serviceProviderId: provider.serviceProviderId,
            companyCode: provider.companyCode,
            serviceProviderName: provider.serviceProviderName,
          })),
    );

    if (documentMetadata?.succeeded && documentMetadata.isDocumentValid) {
      const response = await mutateAsync({
        requestedServiceProviders: flatProviderList,
      }).catch(toastApiError);

      if (!response) {
        return;
      }

      toast.success(t("powerOfAttorney.billFormModal.success"));

      reset();
      modal.close();
    } else {
      create((modalProps) => (
        <PowerOfAttorneyDokobitConsentModal
          {...modalProps}
          powerOfAttorneyLedgerConsentId={powerOfAttorneyLedgerConsentId}
          overviewData={overviewData}
          important
          modalStack={[modal.id]}
        />
      ));
    }
  };

  const onAddHouseClick = () => {
    create((modalProps) => <AddHouseModal {...modalProps} />);
  };

  const openPdf = () => {
    if (signedDocument?.succeeded) {
      openPdfPreview(signedDocument.fileName, signedDocument.content);
    }
  };

  const placeholderCount = overviewData?.jointBillServices.length ?? 1;
  const isLoading = isReccomendationQueryLoading || !overviewData;

  return (
    <Modal
      modal={modal}
      title={t("powerOfAttorney.billFormModal.title")}
      subtitle={t("powerOfAttorney.billFormModal.subtitle")}
      className="md:max-w-3xl"
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("powerOfAttorney.billFormModal.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          >
            {t("powerOfAttorney.billFormModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      {isLoading && (
        <div className="absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-white">
          <ClassicLoader />
        </div>
      )}
      <span className="mt-12 text-sm font-semibold">
        {t("powerOfAttorney.billFormModal.tickBoxes")}
      </span>
      <div className="mt-3 flex flex-col gap-4">
        {!isLoading && useRecommendationQueryFn
          ? overviewData.jointBillServices.map((service, index) => (
              <PowerOfAttorneyHouseAccordionDataWrapper
                key={service.jointBillServiceId}
                useRecommendationQueryFn={
                  index === 0
                    ? useRecommendationQueryFn
                    : useFallbackRecommendationQueryFn
                }
                jointBillService={service}
                showExistingConsent={showExistingConsent}
                powerOfAttorneyLedgerConsentId={powerOfAttorneyLedgerConsentId}
                includeRecommendations={
                  !!shouldFetchAllRecommendations ||
                  !!powerOfAttorneyLedgerConsentId
                }
                isFirst={index === 0}
                recommendationSource={
                  index === 0
                    ? reccomendationSource
                    : POAServiceProviderSource.GenericRecommendation
                }
              />
            ))
          : Array.from({ length: placeholderCount }).map((_, index) => (
              <Skeleton key={index} className="h-[102px] rounded-2xl" />
            ))}
      </div>
      <div className="mt-8 flex flex-col gap-4">
        <span className="text-sm text-grey-dark">
          {t("powerOfAttorney.billFormModal.addHouse.label")}
        </span>
        <Button
          variant="secondary"
          icon={PlusIcon}
          className="bg-blue-light"
          onClick={onAddHouseClick}
        >
          {t("powerOfAttorney.billFormModal.addHouse.button")}
        </Button>
      </div>
      {documentMetadata?.succeeded && (
        <div className="mt-4 flex flex-col gap-2 text-sm">
          <span>
            {t("powerOfAttorney.billFormModal.dateOfConsent")}{" "}
            {formatDate(
              documentMetadata.dateOfSigning,
              t("powerOfAttorney.billFormModal.dateFormat"),
            )}
          </span>
          <span>
            {t("powerOfAttorney.billFormModal.validTo")}{" "}
            <span
              className={twMerge(
                isAfter(new Date(), new Date(documentMetadata.validTo)) &&
                  "text-red-dark",
              )}
            >
              {formatDate(
                documentMetadata.validTo,
                t("powerOfAttorney.billFormModal.dateFormat"),
              )}
            </span>
          </span>
        </div>
      )}
      {signedDocument?.succeeded && (
        <Button className="pt-5" variant="link" onClick={openPdf}>
          {t("powerOfAttorney.billFormModal.downloadSignedDocument")}
        </Button>
      )}
      <div className="mt-9 flex flex-col gap-2">
        <FormCheckbox
          control={control}
          name="authorization"
          label={t(
            "powerOfAttorney.billFormModal.checkbox.authorization.label",
            { email: customerDetails?.billForwardEmail },
          )}
          disabled={showExistingConsent}
        />
        <FormCheckbox
          control={control}
          name="consent"
          label={t("powerOfAttorney.billFormModal.checkbox.consent.label")}
          disabled={showExistingConsent}
        />
        <FormCheckbox
          control={control}
          name="privacy"
          label={
            <Trans
              i18nKey="powerOfAttorney.billFormModal.checkbox.privacy.label"
              components={{
                href: (
                  <Link
                    to={t(
                      "powerOfAttorney.billFormModal.checkbox.privacy.link",
                    )}
                    target="__blank"
                  />
                ),
              }}
            />
          }
          disabled={showExistingConsent}
        />
      </div>
    </Modal>
  );
};
